import React, { useEffect, useReducer } from 'react'

import { useQuery } from '../hooks/useQuery'
import isEqual from 'lodash/isEqual'
import usePrevious from '../../hooks/common/usePrevious'
import get from 'lodash/get'

/**
 * @typedef {{
 *   searchTerm: string;
 *   page: number;
 *   radius?: number;
 *   location?: object;
 *   workFromHome?: boolean;
 * }} JobSearchParams
 */

/**
 * @typedef {{
 *   searchTerm: string;
 *   radius?: number;
 *   location?: object;
 *   workFromHome?: boolean;
 * }} FormValues
 */

function reducer(state, newState) {
  return {
    ...state,
    ...newState,
  }
}

const initialState = {
  nextPageToken: null,
  loadedJobPostings: [],
  noMorePagesToLoad: false,
  variables: {},
}

const withJobSearch = BaseComponent => props => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const PAGE_SIZE = 10

  const operation = 'jobs'
  const defaultVariables = {
    searchTerm: { value: '', required: true },
    location: { value: '', required: false },
    radius: { value: 0, required: false },
    nextPageToken: { value: 0, required: true },
    workFromHome: { value: true, required: false },
  }

  const { data, refetch, loading, error } = useQuery(operation, defaultVariables, {
    lazy: true,
  })

  const nextPageToken = get(data, 'nextPageToken', null) // Get next page token
  const jobs = get(data, 'jobs', [])

  // Runs when we get new batch of job postings
  // Update pagination params and loaded job postings
  const prevData = usePrevious(data)
  useEffect(() => {
    if (!isEqual(data, prevData) && data) {
      const noResults = state.indexToLoadFrom === 0 && !jobs.length
      const wasLastPage = jobs.length === 0 || jobs.length % PAGE_SIZE !== 0

      dispatch({
        loadedJobPostings: [...state.loadedJobPostings, ...jobs],
        nextPageToken,
        noMorePagesToLoad: wasLastPage,
        noResults,
      })
    }
  }, [data, prevData])

  /**
   * For checking if search criteria have changed
   * Not Checking pagination criteria here
   *
   * @param {FormValues} params
   */
  const getVariables = params => {
    const { radius, location, workFromHome, searchTerm } = params

    const variables = { searchTerm }
    if (radius) variables.radius = radius
    if (location) variables.location = location
    if (workFromHome) variables.workFromHome = workFromHome

    return variables
  }

  /**
   * For Getting Final Variables for Submission
   * @returns {JobSearchParams}
   */
  const getVariablesForSearch = params => {
    return {
      ...getVariables(params),
      nextPageToken: nextPageToken ? nextPageToken : 0,
    }
  }

  // Only run this when Search Button is clicked and search criteria have changed, otherwise `getNextBatchOfJobPostings` function
  const getJobPostings = params => {
    const variables = getVariables(params)
    if (isEqual(variables, state.variables)) {
      return
    }
    dispatch({
      variables,
      indexToLoadFrom: 0,
      nextPageToken: nextPageToken ? nextPageToken : 0,
      loadedJobPostings: [],
    })
    refetch({ ...variables, page: 0 })
  }

  // Load a new batch ("Load More Button")
  const getNextBatchOfJobPostings = params => {
    /**
     * @type {JobSearchParams}
     */
    const variables = getVariablesForSearch(params)

    refetch(variables)
  }

  return (
    <BaseComponent
      {...props}
      getJobPostings={getJobPostings}
      loadingJobPostings={loading}
      jobPostings={state.loadedJobPostings}
      getNextBatchOfJobPostings={getNextBatchOfJobPostings}
      noMorePagesToLoad={state.noMorePagesToLoad}
      noResults={state.noResults}
      error={error}
    />
  )
}

export default withJobSearch



// WEBPACK FOOTER //
// ./src/utils/graphql/hocs/withJobSearch.js